// Plain-language helpers for the AI budget shown in the chat.
// The server (api/_aiUsage.js) sends a canopy_usage object; this file reads it safely,
// decides whether the owner is fine, running low, or out, and picks the words to show.

export const LOW_BUDGET_SHARE = 0.1; // "low" means less than 10% of the budget remains

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

const wholeCents = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0;

export const centsToDollars = (cents) => `$${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;

// "2026-11-01" -> "November 1". Anything else -> "".
export function resetLabel(resetsOn) {
  const m = typeof resetsOn === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(resetsOn) : null;
  if (!m) return '';
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  return `${MONTHS[month - 1]} ${day}`;
}

// Returns a clean object, or null if anything important is missing or not a number.
export function parseCanopyUsage(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const { spent_cents: spent, limit_cents: limit, remaining_cents: remaining } = raw;
  if (!wholeCents(spent) || !wholeCents(limit) || !wholeCents(remaining) || limit === 0) return null;
  return {
    spentCents: spent,
    limitCents: limit,
    remainingCents: remaining,
    resetsLabel: resetLabel(raw.resets_on),
    summary: typeof raw.summary === 'string' ? raw.summary.trim() : '',
  };
}

// 'unknown' (nothing to show), 'ok', 'low' (under 10% left) or 'used_up'.
export function budgetState(usage) {
  if (!usage) return 'unknown';
  if (usage.remainingCents <= 0) return 'used_up';
  if (usage.remainingCents < usage.limitCents * LOW_BUDGET_SHARE) return 'low';
  return 'ok';
}

// The quiet line under the chat box. Empty when there is nothing to say.
export function budgetLine(usage) {
  if (!usage) return '';
  if (usage.remainingCents <= 0) return usedUpMessage(usage);
  if (usage.summary) return usage.summary;
  const reset = usage.resetsLabel ? ` Resets ${usage.resetsLabel}.` : '';
  return `${centsToDollars(usage.remainingCents)} of your ${centsToDollars(usage.limitCents)} AI budget left this month.${reset}`;
}

export function usedUpMessage(usage) {
  const reset = usage && usage.resetsLabel ? `It resets on ${usage.resetsLabel}.` : 'It resets next month.';
  return `You've used this month's AI budget. ${reset}`;
}

// A 429 with canopy_usage means the budget is used up. A 429 without it is Anthropic being
// busy. Returns the parsed usage for the first case and null for everything else.
export function usedUpFromResponse(status, data) {
  if (status !== 429 || !data || typeof data !== 'object') return null;
  const usage = parseCanopyUsage(data.canopy_usage);
  // The server refused because the budget is gone, so treat it as empty even if cents remain.
  return usage ? { ...usage, remainingCents: 0 } : null;
}

// A budget result that arrives late must not undo a "used up" answer we already have.
// prev: the budget on screen (or null). next: the result that just arrived. Returns the one to keep.
export function keepUsedUp(prev, next) {
  return budgetState(prev) === 'used_up' ? prev : next;
}
